import React, { useState, useEffect } from 'react';
import { XMarkIcon, DocumentIcon, CubeIcon, TagIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { Product, ProductDocument, productsAPI, API_BASE_URL } from '../../services/api';

interface ProductDetailProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
}

const ProductDetail: React.FC<ProductDetailProps> = ({ isOpen, onClose, product }) => {
  const [documents, setDocuments] = useState<ProductDocument[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && product) {
      fetchDocuments();
    } else {
      setDocuments([]);
    }
  }, [isOpen, product]);

  const fetchDocuments = async () => {
    if (!product) return;
    try {
      setLoading(true);
      const docs = await productsAPI.getDocuments(product.id);
      setDocuments(docs);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const getFileTypeColor = (fileType: string) => {
    switch (fileType) {
      case 'SPECIFICATION':
        return 'bg-primary-100 text-primary-800';
      case 'CERTIFICATE':
        return 'bg-success-100 text-success-800';
      case 'DATASHEET':
        return 'bg-secondary-100 text-secondary-800';
      case 'INVOICE':
        return 'bg-warning-100 text-warning-800';
      case 'CONTRACT':
        return 'bg-error-100 text-error-800';
      case 'IMAGE':
        return 'bg-secondary-100 text-secondary-800';
      case 'COA':
        return 'bg-success-100 text-success-800';
      case 'MSDS':
        return 'bg-warning-100 text-warning-800';
      default:
        return 'bg-neutral-100 text-neutral-800';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getDocumentUrl = (fileUrl: string) => {
    if (!fileUrl) return '#';
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
    return `${API_BASE_URL}/${fileUrl.replace(/\\/g, '/')}`;
  };

  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 bg-neutral-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-primary-600 to-primary-700">
          <div className="flex items-center">
            <CubeIcon className="h-8 w-8 text-white mr-3" />
            <div>
              <h3 className="text-xl font-semibold text-white">{product.name}</h3>
              <p className="text-sm text-primary-100">SKU: {product.sku}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:text-primary-100 transition-colors">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h4 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
              <TagIcon className="h-5 w-5 mr-2 text-primary-600" />
              Basic Information
            </h4>
            <div className="grid grid-cols-2 gap-4 bg-neutral-50 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-neutral-500">SKU</label>
                <p className="mt-1 text-sm text-neutral-900">{product.sku}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">Name</label>
                <p className="mt-1 text-sm text-neutral-900">{product.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">Category</label>
                <p className="mt-1 text-sm text-neutral-900">{product.category?.name || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">Supplier</label>
                <p className="mt-1 text-sm text-neutral-900">{product.supplier?.name || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">Unit</label>
                <p className="mt-1 text-sm text-neutral-900">{product.unit || '-'}</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-neutral-500">Description</label>
                <p className="mt-1 text-sm text-neutral-900">{product.description || '-'}</p>
              </div>
            </div>
          </div>

          {/* Pricing Information */}
          {product.costRanges && product.costRanges.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                <CurrencyDollarIcon className="h-5 w-5 mr-2 text-primary-600" />
                Cost Ranges
              </h4>
              <div className="bg-neutral-50 p-4 rounded-lg">
                <table className="min-w-full divide-y divide-neutral-300">
                  <thead>
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Min Qty</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Max Qty</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Unit</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Price/Unit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200">
                    {product.costRanges.map((range, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2 text-sm text-neutral-900">{range.minQuantity}</td>
                        <td className="px-3 py-2 text-sm text-neutral-900">{range.maxQuantity || 'Unlimited'}</td>
                        <td className="px-3 py-2 text-sm text-neutral-900">{range.unit || product.unit || '-'}</td>
                        <td className="px-3 py-2 text-sm text-neutral-900">${parseFloat(range.pricePerUnit).toFixed(2)} {range.currency}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div>
            <div className="grid grid-cols-2 gap-4 bg-neutral-50 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-neutral-500">Status</label>
                <span
                  className={`inline-flex mt-1 px-2 py-1 text-xs font-medium rounded-full ${
                    product.isActive ? 'bg-success-100 text-success-800' : 'bg-neutral-100 text-neutral-800'
                  }`}
                >
                  {product.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">Inventory Items</label>
                <p className="mt-1 text-sm text-neutral-900">{product._count?.inventoryItems || 0}</p>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div>
            <h4 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
              <DocumentIcon className="h-5 w-5 mr-2 text-primary-600" />
              Documents ({documents.length})
            </h4>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : documents.length > 0 ? (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-200 hover:bg-neutral-100 transition-colors"
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <DocumentIcon className="h-5 w-5 text-neutral-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-900 truncate">{doc.fileName}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getFileTypeColor(doc.fileType)}`}>
                            {doc.fileType}
                          </span>
                          <span className="text-xs text-neutral-500">{formatFileSize(doc.fileSize)}</span>
                          <span className="text-xs text-neutral-500">
                            {new Date(doc.uploadedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <a
                      href={getDocumentUrl(doc.fileUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-4 px-3 py-1 text-xs font-medium text-primary-600 bg-primary-50 rounded hover:bg-primary-100 transition-colors"
                    >
                      Download
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-neutral-50 rounded-lg">
                <DocumentIcon className="mx-auto h-12 w-12 text-neutral-400" />
                <p className="mt-2 text-sm text-neutral-500">No documents uploaded</p>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="border-t pt-4">
            <div className="grid grid-cols-2 gap-4 text-sm text-neutral-500">
              <div>
                <span className="font-medium">Created:</span> {new Date(product.createdAt).toLocaleString()}
              </div>
              <div>
                <span className="font-medium">Updated:</span> {new Date(product.updatedAt).toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t bg-neutral-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
